import {
  Checkbox,
  ConfigProvider,
  DatePicker,
  InputNumber,
  Popover,
  Radio,
  Select,
  Space,
  Tag,
  Typography,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  ALL_TRAININGS_OPTION,
  DATE_RANGES,
  DEFAULT_LATEST_N,
  LATEST_N_MAX,
  LATEST_N_MIN,
  TRAINING_COUNT_RANGES,
  usesSessionWindow,
  WEEKDAY_TAGS,
  GAMEDAY_TAGS,
} from "../constants";
import { ControllerBindingRow } from "./ControllerBindingRow";
import { useControllers } from "../../Contexts/dashboardHooks";
import { boundController } from "../../Processing/controllerBindings";
import { resolveDateRange } from "../../Utils/resolveDateRange";

const { Text } = Typography;

export interface DateRangeFormFields {
  /** UUID of the Slicer this date section follows, when it is bound. */
  dateControllerId?: string;
  dateRange: string;
  showAdvancedDate: boolean;
  startDateType: "relative" | "absolute" | "latest_n";
  startDateAbsolute: string;
  startDateOffset: number;
  /** How many dates with data the window holds, when startDateType is latest_n. */
  startDateLatestN?: number;
  endDateType: "relative" | "absolute";
  endDateAbsolute: string;
  endDateOffset: number;
  dayFilter: string[];
}

interface DateRangeFormProps {
  value: DateRangeFormFields;
  onChange: (patch: Partial<DateRangeFormFields>) => void;
  description?: string;
  /** Show the "Dynamically controlled" row — the host widget's `dynamic.bindableSections`. */
  bindable?: boolean;
  /**
   * Tile mode (the on-dashboard controller). The custom-range editor is far taller than a
   * compressed tile, so it collapses to a one-line summary chip; hovering (or clicking)
   * the chip opens the full editor in a popover that overlays the dashboard.
   */
  compact?: boolean;
  /** Control size. The slicer widget passes "middle"; the Add Widget modal keeps "small". */
  size?: "small" | "middle";
  /** Fires as any dropdown, date picker or the custom-range popover opens or closes. */
  onPopupOpenChange?: (open: boolean) => void;
  /** Fires when a typed field loses focus — the coach is done with that value. */
  onSettle?: () => void;
  /**
   * Offer the session window — the Advanced mode and the training-count presets.
   *
   * Off by default, because the backend takes it on chart, table, table_2d, pie, gauge,
   * radar and the slicer only. A stat tile already carries "last N" in its comparison,
   * player notes have no metric, and a body map reads one date.
   *
   * It also decides what a BOUND widget shows: one that cannot hold the window keeps its
   * own dates, because that is what it queries.
   */
  allowLatestN?: boolean;
  /**
   * Why this widget cannot take a slicer's session window — passed to the binding row.
   * Only the types that reject the window set it (stat tile, player notes).
   */
  keepsOwnDatesOnLatestN?: string;
}

export const DATE_RANGE_DEFAULTS: DateRangeFormFields = {
  dateRange: "last4weeks",
  showAdvancedDate: false,
  startDateType: "relative",
  startDateAbsolute: "",
  startDateOffset: -28,
  startDateLatestN: DEFAULT_LATEST_N,
  endDateType: "relative",
  endDateAbsolute: "",
  endDateOffset: 0,
  dayFilter: [],
};

export function DateRangeForm({
  value,
  onChange,
  description = "Set the time window for this data.",
  bindable = false,
  compact = false,
  size = "small",
  onPopupOpenChange,
  onSettle,
  allowLatestN = false,
  keepsOwnDatesOnLatestN,
}: DateRangeFormProps) {
  const toggleDayFilter = (val: string) => {
    const next = value.dayFilter.includes(val)
      ? value.dayFilter.filter((v) => v !== val)
      : [...value.dayFilter, val];
    onChange({ dayFilter: next });
  };

  // While bound, the controls show the CONTROLLER's dates rather than this widget's own.
  // The widget's values stay untouched underneath and come back when the box is unchecked.
  const controllers = useControllers();
  const controller = bindable
    ? boundController(value.dateControllerId, "date", controllers)
    : undefined;
  const controllerMisc = controller?.spec.slicer_builder_misc;
  // The backend declines the substitution for a widget type that cannot hold the window,
  // so the widget queries its own dates. Show those, or the picker describes a range the
  // widget is not using and offers no way back to the one it is.
  const controllerUsesWindow = usesSessionWindow(controllerMisc);
  const isBound = controller != null && !(controllerUsesWindow && !allowLatestN);
  const shown: DateRangeFormFields = isBound
    ? {
        ...value,
        dateRange: controllerMisc?.dateRange ?? DATE_RANGE_DEFAULTS.dateRange,
        showAdvancedDate: controllerMisc?.showAdvancedDate ?? DATE_RANGE_DEFAULTS.showAdvancedDate,
        startDateAbsolute: controllerMisc?.startDateAbsolute ?? DATE_RANGE_DEFAULTS.startDateAbsolute,
        startDateOffset: controllerMisc?.startDateOffset ?? DATE_RANGE_DEFAULTS.startDateOffset,
        startDateType: controllerMisc?.startDateType ?? DATE_RANGE_DEFAULTS.startDateType,
        startDateLatestN: controllerMisc?.startDateLatestN ?? DATE_RANGE_DEFAULTS.startDateLatestN,
        endDateType: controllerMisc?.endDateType ?? DATE_RANGE_DEFAULTS.endDateType,
        endDateAbsolute: controllerMisc?.endDateAbsolute ?? DATE_RANGE_DEFAULTS.endDateAbsolute,
        endDateOffset: controllerMisc?.endDateOffset ?? DATE_RANGE_DEFAULTS.endDateOffset,
        dayFilter: controllerMisc?.dayFilter ?? DATE_RANGE_DEFAULTS.dayFilter,
      }
    : value;

  // Popups inside the hover popover must mount INSIDE it — a dropdown portaled to
  // document.body sits outside the hover area, so opening it would close the popover.
  const popupContainer = compact
    ? (trigger: HTMLElement) => trigger.parentElement ?? document.body
    : undefined;

  // Advanced date filters IS a latest_n start date, so the mode needs no field of its own.
  const isLatestN = allowLatestN && shown.startDateType === "latest_n";

  const dateEdgeRows = (
    <>
      {(["start", "end"] as const).map((edge) => {
        const typeKey = edge === "start" ? "startDateType" : "endDateType";
        const absKey = edge === "start" ? "startDateAbsolute" : "endDateAbsolute";
        const offsetKey = edge === "start" ? "startDateOffset" : "endDateOffset";
        const label = edge === "start" ? "From" : "To";

        return (
          <div key={edge}>
            <Space size={8} align="center" wrap>
              <Text strong style={{ fontSize: 11, minWidth: 36, whiteSpace: "nowrap" }}>{label}</Text>
              <Select
                size={size}
                value={shown[typeKey]}
                onChange={(v) => onChange({ [typeKey]: v })}
                onOpenChange={onPopupOpenChange}
                style={{ width: 100 }}
                {...(popupContainer && { getPopupContainer: popupContainer })}
                options={[
                  { value: "relative", label: "Relative" },
                  { value: "absolute", label: "Absolute" },
                ]}
              />
              {shown[typeKey] === "relative" ? (
                <>
                  <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>today</Tag>
                  <InputNumber
                    size={size}
                    value={shown[offsetKey]}
                    onChange={(v) => onChange({ [offsetKey]: v ?? 0 })}
                    onBlur={onSettle}
                    min={-365}
                    max={365}
                    style={{ width: 64 }}
                  />
                  <Text type="secondary" style={{ fontSize: 10 }}>days</Text>
                </>
              ) : (
                <DatePicker
                  size={size}
                  value={shown[absKey] ? dayjs(shown[absKey], "YYYY-MM-DD") : null}
                  {...(popupContainer && { getPopupContainer: popupContainer })}
                  onChange={(_d, ds) =>
                    onChange({ [absKey]: typeof ds === "string" ? ds : "" })
                  }
                  onOpenChange={onPopupOpenChange}
                />
              )}
            </Space>
          </div>
        );
      })}
    </>
  );

  const dayFilterOptions = [
    // First, and selected whenever nothing else is: an empty dayFilter already means
    // "every date with data", so this names the default rather than adding a state.
    { value: ALL_TRAININGS_OPTION, label: "Trainings" },
    {
      label: <Text strong style={{ fontSize: 11 }}>Day of week</Text>,
      options: WEEKDAY_TAGS.map((t) => ({ value: t.value, label: t.label })),
    },
    {
      label: (
        <>
          <Text strong style={{ fontSize: 11 }}>Game day proximity</Text>
          <Text
            type="secondary"
            style={{ fontSize: 10, fontWeight: 400, display: "block",
                     whiteSpace: "normal", lineHeight: 1.35 }}
          >
            Days relative to a match — GD is game day, GD−1 is the day before, etc.
          </Text>
        </>
      ),
      options: GAMEDAY_TAGS.map((t) => ({ value: t.value, label: t.label })),
    },
  ];

  const latestNRow = (
    <>
    <Space size={8} align="center" wrap>
      <Text strong style={{ fontSize: 11, whiteSpace: "nowrap" }}>Latest</Text>
      <InputNumber
        size={size}
        value={shown.startDateLatestN ?? DEFAULT_LATEST_N}
        onChange={(v) =>
          onChange({ startDateLatestN: v == null ? undefined : Math.round(Number(v)) })
        }
        onBlur={() => {
          // antd clamps on blur, but the slicer tile autosaves on an idle timer, so an
          // out-of-range count would reach the backend and fail the whole dashboard.
          const n = shown.startDateLatestN;
          const clamped = Math.min(LATEST_N_MAX, Math.max(LATEST_N_MIN, n ?? DEFAULT_LATEST_N));
          if (clamped !== n) onChange({ startDateLatestN: clamped });
          onSettle?.();
        }}
        min={LATEST_N_MIN}
        max={LATEST_N_MAX}
        style={{ width: 64 }}
      />
      {/* Writes the SAME dayFilter the tag list writes, so saving and preselection ride
          on the mechanism that is already there. */}
      {/* antd's small tag is 16px tall around 14px text, so the text fills the pill. */}
      <ConfigProvider theme={{ components: { Select: { multipleItemHeightSM: 20 } } }}>
      <Select
        mode="multiple"
        size={size}
        value={shown.dayFilter.length === 0 ? [ALL_TRAININGS_OPTION] : shown.dayFilter}
        onChange={(picked) => {
          // Picking Trainings while real tags are selected means "drop the day filter",
          // so it clears rather than joining them. Picking a real tag while Trainings is
          // showing just stores that tag, and Trainings falls away on its own.
          const choseTrainings =
            picked.includes(ALL_TRAININGS_OPTION) && shown.dayFilter.length > 0;
          onChange({
            dayFilter: choseTrainings
              ? []
              : picked.filter((v) => v !== ALL_TRAININGS_OPTION),
          });
        }}
        onOpenChange={onPopupOpenChange}
        allowClear
        style={{ minWidth: 240, maxWidth: 240, flex: 1 }}
        options={dayFilterOptions}
        {...(popupContainer && { getPopupContainer: popupContainer })}
      />
      </ConfigProvider>
    </Space>
    <Text type="secondary" style={{ fontSize: 10, display: "block", marginTop: 2 }}>
      most recent dates with data, ending today
    </Text>
    </>
  );

  const advancedFields = (
    <>
      {allowLatestN && (
        <Radio.Group
          value={isLatestN ? "latest_n" : "range"}
          onChange={(e) =>
            onChange(
              e.target.value === "latest_n"
                ? {
                    startDateType: "latest_n",
                    startDateLatestN: shown.startDateLatestN ?? DEFAULT_LATEST_N,
                  }
                // Back to a calendar window. An absolute start cannot be restored — the
                // mode kept no date — so the picker returns to relative.
                : { startDateType: "relative" },
            )
          }
        >
          <Space direction="vertical" size={2}>
            <Radio value="range" style={{ fontSize: 12 }}>Simple date range</Radio>
            <Radio value="latest_n" style={{ fontSize: 12 }}>Advanced date filters</Radio>
          </Space>
        </Radio.Group>
      )}

      {isLatestN ? latestNRow : dateEdgeRows}

      {!isLatestN && (
      <>
      <div style={{ height: 1, background: "#e0e5eb", margin: "2px 0" }} />

      <div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
          <Text strong style={{ fontSize: 11 }}>Include only these days</Text>
          <Text type="secondary" style={{ fontSize: 10 }}>
            {shown.dayFilter.length === 0 ? "All days included" : `${shown.dayFilter.length} selected`}
          </Text>
        </div>

        <Text type="secondary" style={{ fontSize: 10, display: "block", marginBottom: 4 }}>
          Day of week
        </Text>
        <Space size={3} wrap>
          {WEEKDAY_TAGS.map((t) => (
            <Tag.CheckableTag
              key={t.value}
              checked={shown.dayFilter.includes(t.value)}
              onChange={() => toggleDayFilter(t.value)}
            >
              {t.label}
            </Tag.CheckableTag>
          ))}
        </Space>

        <Text
          type="secondary"
          style={{ fontSize: 10, display: "block", marginTop: 6, marginBottom: 2 }}
        >
          Game day proximity
        </Text>
        <Text
          type="secondary"
          style={{ fontSize: 10, display: "block", lineHeight: 1.35, marginBottom: 4 }}
        >
          Days relative to a match — GD is game day, GD−1 is the day before, etc.
        </Text>
        <Space size={3} wrap>
          {GAMEDAY_TAGS.map((t) => (
            <Tag.CheckableTag
              key={t.value}
              checked={shown.dayFilter.includes(t.value)}
              onChange={() => toggleDayFilter(t.value)}
            >
              {t.label}
            </Tag.CheckableTag>
          ))}
        </Space>
      </div>
      </>
      )}
    </>
  );

  const summary =
    resolveDateRange(shown) +
    (shown.dayFilter.length > 0
      ? ` · ${shown.dayFilter.length} day filter${shown.dayFilter.length > 1 ? "s" : ""}`
      : "");

  return (
    <div className="awm-step-body">
      {bindable && (
        <ControllerBindingRow
          kind="date"
          controllerId={value.dateControllerId}
          onChange={(dateControllerId) => onChange({ dateControllerId })}
          keepsOwnDatesOnLatestN={keepsOwnDatesOnLatestN}
        />
      )}

      <div className={`awm-form-stack${isBound ? " awm-bound-section" : ""}`}>
      {description && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {description}
        </Text>
      )}

      {!shown.showAdvancedDate && (
        <div className="awm-labeled-row">
          <Text className="awm-labeled-row-label" style={{ fontSize: 11 }}>Date range</Text>
          <Select
            size={size}
            value={shown.dateRange}
            onChange={(v) => onChange({ dateRange: v })}
            onOpenChange={onPopupOpenChange}
            options={[
              ...DATE_RANGES.map((r) => ({ value: r.value, label: r.label })),
              // A slicer must never see these: the backend builds its dates from this
              // preset string, and it can only make a calendar date out of it.
              ...(allowLatestN
                ? TRAINING_COUNT_RANGES.map((r) => ({ value: r.value, label: r.label }))
                : []),
            ]}
          />
        </div>
      )}

      <Checkbox
        checked={shown.showAdvancedDate}
        onChange={(e) => onChange({ showAdvancedDate: e.target.checked })}
      >
        <Text className="awm-advanced-date-toggle">
          {compact ? "Custom range" : allowLatestN ? "Use a custom window" : "Use custom date range"}
        </Text>
      </Checkbox>

      {shown.showAdvancedDate && (compact ? (
        <Popover
          content={
            <div style={{ display: "flex", flexDirection: "column", gap: 8, width: allowLatestN ? 344 : 300, maxWidth: "80vw" }}>
              {advancedFields}
            </div>
          }
          trigger={["hover", "click"]}
          mouseEnterDelay={0.15}
          mouseLeaveDelay={0.4}
          placement="bottomLeft"
          onOpenChange={onPopupOpenChange}
        >
          <div className="awm-custom-range-summary">
            <Text className="awm-custom-range-summary-text">{summary}</Text>
            <EditOutlined style={{ fontSize: 11, flexShrink: 0 }} />
          </div>
        </Popover>
      ) : (
        <div
          style={{
            padding: 10, border: "1px solid #d0d8e0", borderRadius: 8,
            background: "#f9fafb", display: "flex", flexDirection: "column", gap: 8,
          }}
        >
          {advancedFields}
        </div>
      ))}
      </div>
    </div>
  );
}
